import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GaugeCircle, Loader2, TrendingUp, Users, BookOpen } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-secondary p-12 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <img src="/sifcore-logo.jpeg" alt="SIFCORE" className="h-11 w-11 rounded-lg object-cover" />
          <div>
            <p className="font-display text-lg font-bold tracking-tight">SIFCORE</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">CRM</p>
          </div>
        </div>

        <div className="max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Run your Salesforce training business on one platform.
          </h2>
          <p className="mt-4 text-slate-400">
            Track leads, close deals, and manage every enrollment across your programs.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: Users, text: "Leads, students & admissions in one place" },
              { icon: TrendingUp, text: "Live revenue & payment insights" },
              { icon: BookOpen, text: "Courses, batches & training programs" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5">
                  <f.icon className="h-4 w-4 text-accent" />
                </div>
                <span className="text-sm text-slate-300">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-500">© {new Date().getFullYear()} SIFCORE. Salesforce training, elevated.</p>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-background px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <img src="/sifcore-logo.jpeg" alt="SIFCORE" className="h-11 w-11 rounded-lg object-cover" />
              <p className="font-display text-lg font-bold text-secondary">SIFCORE CRM</p>
            </div>
          </div>

          <h1 className="font-display text-2xl font-bold tracking-tight text-secondary">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your workspace to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="you@sifcore.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
